import { useTranslation } from "react-i18next";
import { SearchHistoryDeleteButton } from "./SearchHistoryDeleteButton";
import { ISearchHistoryInfoType } from "../types/search_history_type";
import { useNavigate } from "react-router-dom";
import { PAGE_NAME_FEEDBACK } from "../../../../constants/common_constants";
import { useApi, useErrorDialog } from "../../../../hooks";
import { IInferenceFactorItemListType } from "../../select_phenomenon/types/select_phenomenon_type";
import { useDtsAppContext } from "../../../../components/provider/DtsAppContextProvider";
import { SetStateAction } from "react";
import { getSearchHistoryDetail } from "../../../api/dts_api_wrapper";

interface SearchHistoryListProps {
  searchHistoryInfo: ISearchHistoryInfoType;
  showConfirmDialog: (message: string) => void;
  userId: string;
  setSearchHistoryId: React.Dispatch<SetStateAction<string>>;
  isLastList: boolean;
}

export const SearchHistoryList: React.FC<SearchHistoryListProps> = ({
  searchHistoryInfo,
  showConfirmDialog,
  userId,
  setSearchHistoryId,
  isLastList,
}) => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  const { handleApi } = useApi();

  const {
    setDacInferenceFactor,
    setDacSearchHistoryId,
    setDacSubjectName,
    setDacModel,
    setDacMachineNumber,
  } = useDtsAppContext();

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //検索日時の表示をUTCからの変換しフォーマット
  const utcYear = Number(searchHistoryInfo.searchDateTime.slice(0, 4));
  const utcMonth = Number(searchHistoryInfo.searchDateTime.slice(4, 6)) - 1;
  const utcDate = Number(searchHistoryInfo.searchDateTime.slice(6, 8));
  const utcHour = Number(searchHistoryInfo.searchDateTime.slice(8, 10));
  const utcMinute = Number(searchHistoryInfo.searchDateTime.slice(10, 12));
  const utcSecond = Number(searchHistoryInfo.searchDateTime.slice(12, 14));
  const utcTime = new Date(
    Date.UTC(utcYear, utcMonth, utcDate, utcHour, utcMinute, utcSecond)
  );
  const localYear = String(utcTime.getFullYear());
  const localMonth = String(utcTime.getMonth() + 1).padStart(2, "0");
  const localDate = String(utcTime.getDate()).padStart(2, "0");
  const localHour = String(utcTime.getHours()).padStart(2, "0");
  const localMinute = String(utcTime.getMinutes()).padStart(2, "0");
  const localSecond = String(utcTime.getSeconds()).padStart(2, "0");

  const formattedDateTime = `${localYear}/${localMonth}/${localDate} ${localHour}:${localMinute}:${localSecond}`;

  const handleOnSearchHistoryList = async () => {
    //何らかの理由でuserId または searchHistoryIdが読み込めなかった
    if (!userId || !searchHistoryInfo.searchHistoryId) {
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage"
      ); //エラー", "通信エラーが発生しました。(トップページに遷移)
      return; // パラメータ不足の場合は処理を終了
    }
    try {
      console.log("handleOnSearchHistoryList start");
      //検索履歴情報詳細の取得
      const searchHistoryDetailResult = await handleApi(
        getSearchHistoryDetail,
        userId,
        searchHistoryInfo.searchHistoryId
      );
      //検索履歴情報詳細取得エラーの場合
      if ("errorMessage" in searchHistoryDetailResult) {
        console.log("errorMessage: ", searchHistoryDetailResult.errorMessage);
        throw new Error("failed to get searchHistory detail info");
      }

      //ソートした検索履歴情報詳細をContext+ローカルストレージに保存
      sortSearchHistiryDetail(searchHistoryDetailResult);
      const searchHistoryDetailStr = JSON.stringify(searchHistoryDetailResult);
      setDacInferenceFactor(searchHistoryDetailStr);
      localStorage.setItem("inferenceFactor", searchHistoryDetailStr);

      //検索履歴情報ID, 案件名, 型式, 機番をContext+ローカルストレージに保存
      setDacSearchHistoryId(searchHistoryInfo.searchHistoryId);
      localStorage.setItem(
        "searchHistoryId",
        searchHistoryInfo.searchHistoryId
      );
      setDacSubjectName(searchHistoryInfo.subjectName);
      localStorage.setItem("subjectName", searchHistoryInfo.subjectName);
      setDacModel(searchHistoryInfo.model);
      localStorage.setItem("model", searchHistoryInfo.model);
      if (searchHistoryInfo.machineNumber) {
        setDacMachineNumber(searchHistoryInfo.machineNumber);
        localStorage.setItem("machineNumber", searchHistoryInfo.machineNumber);
      } else {
        // 機番未入力の場合、Contextを""に更新+ローカルストレージリセット
        setDacMachineNumber("");
        localStorage.removeItem("machineNumber");
      }

      navigate(`/${PAGE_NAME_FEEDBACK}`);
    } catch (err) {
      console.log("[Search History]handleApi err :", err);
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。")
      ); //エラー", "通信エラーが発生しました。
    }
  };

  return (
    //取得した検索履歴一覧の最後尾の履歴情報の場合, 下の余白は付けない
    <div
      className={`relative w-full bg-white border border-gray-500 ${
        !isLastList && "mb-4"
      }`}
    >
      {
        searchHistoryInfo.status === "1" && (
          // 回答状況(status)が完了(1)の場合にボタン表示
          <SearchHistoryDeleteButton
            showConfirmDialog={showConfirmDialog}
            searchHistoryId={searchHistoryInfo.searchHistoryId}
            setSearchHistoryId={setSearchHistoryId}
          />
        ) // 未完了(0)の場合何も表示しない
      }

      <div className="pl-5 py-3" onClick={handleOnSearchHistoryList}>
        <p className="pb-1">
          {t("検索履歴画面.検索日時")} :{/* 検索日時 */}
          <span className="font-normal pl-1">{formattedDateTime}</span>
        </p>
        <p className="">
          {t("検索履歴画面.案件名")} :{/* 案件名 */}
        </p>
        <p className="font-normal pl-5 pb-1">{searchHistoryInfo.subjectName}</p>
        <p className="pb-1">
          {t("検索履歴画面.型式")} :{/* 型式 */}
          <span className="font-normal pl-1">{searchHistoryInfo.model}</span>
        </p>
        {!!searchHistoryInfo.machineNumber && (
          <p className="pb-1">
            {t("検索履歴画面.機番")} :{/* 機番 */}
            <span className="font-normal pl-1">
              {searchHistoryInfo.machineNumber}
            </span>
          </p>
        )}
        <p>
          {t("検索履歴画面.回答状況")} :{/* 回答状況 */}
          <span className="font-normal pl-1">
            {searchHistoryInfo.status === "1"
              ? t("検索履歴画面.完了")
              : t("検索履歴画面.未完了")}
            {/* 完了/未完了 */}
          </span>
        </p>
      </div>
    </div>
  );
};

/* 取得した検索履歴情報詳細をpriorityに従ってソートする関数 */
const sortSearchHistiryDetail = (
  unsortedData: IInferenceFactorItemListType
) => {
  unsortedData.inferenceFactor.sort((a, b) =>
    a.issueFactor.priority < b.issueFactor.priority ? -1 : 1
  );
  unsortedData.inferenceFactor.map((value) => {
    return value.issueFactor.repairFactor.referenceInfo.sort((a, b) =>
      a.priority < b.priority ? -1 : 1
    );
  });
  return unsortedData;
};
